import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { apiClient } from '@/api/client';
// Removed WebSocketStore dependency - instructor dashboard uses polling instead
import type { StudentSessionInfo, MonitoringResult, AlertLevel } from '@/types';

type ViewMode = 'grid' | 'list' | 'detailed';
type SortField = 'studentName' | 'alertCount' | 'focusPercentage' | 'lastActivity' | 'examStatus';
type SortOrder = 'asc' | 'desc';

interface FilterOptions {
  alertLevel: AlertLevel | 'all';
  examStatus: string | 'all';
  focusThreshold: number; // Minimum focus percentage
  searchTerm: string;
}

interface InstructorState {
  studentSessions: StudentSessionInfo[];
  selectedStudents: Set<string>;
  focusedStudent: string | null;
  isLoading: boolean;
  error: string | null;
  lastRefresh: number | null;
  viewMode: ViewMode;
  sortField: SortField;
  sortOrder: SortOrder;
  filters: FilterOptions;
  realTimeUpdates: boolean;
  refreshInterval: number;
  exportInProgress: boolean;
  dashboardStats: {
    totalStudents: number;
    activeExams: number;
    totalAlerts: number;
    averageFocusPercentage: number;
    alertsByLevel: Record<AlertLevel, number>;
  };
}

interface InstructorActions {
  loadStudentSessions: () => Promise<void>;
  refreshStudentData: () => Promise<void>;
  selectStudent: (sessionId: string) => void;
  selectMultipleStudents: (sessionIds: string[]) => void;
  deselectStudent: (sessionId: string) => void;
  clearSelection: () => void;
  focusOnStudent: (sessionId: string) => void;
  unfocusStudent: () => void;
  setViewMode: (mode: ViewMode) => void;
  setSorting: (field: SortField, order?: SortOrder) => void;
  setFilters: (filters: Partial<FilterOptions>) => void;
  resetFilters: () => void;
  enableRealTimeUpdates: () => void;
  disableRealTimeUpdates: () => void;
  setRefreshInterval: (interval: number) => void;
  exportSelectedSessions: () => Promise<void>;
  exportAllSessions: () => Promise<void>;
  cleanupInactiveSessions: () => Promise<void>;
  resetSession: (sessionId: string) => Promise<void>;
  setError: (error: string | null) => void;
  updateStudentData: (sessionId: string, data: Partial<StudentSessionInfo>) => void;
  addMonitoringUpdate: (sessionId: string, monitoring: MonitoringResult) => void;
  getFilteredStudents: () => StudentSessionInfo[];
  getDashboardStats: () => void;
}

type InstructorStore = InstructorState & InstructorActions;

let refreshIntervalId: NodeJS.Timeout | null = null;

export const useInstructorStore = create<InstructorStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        studentSessions: [],
        selectedStudents: new Set(),
        focusedStudent: null,
        isLoading: false,
        error: null,
        lastRefresh: null,
        viewMode: 'grid',
        sortField: 'studentName',
        sortOrder: 'asc',
        filters: {
          alertLevel: 'all',
          examStatus: 'all',
          focusThreshold: 0,
          searchTerm: '',
        },
        realTimeUpdates: true,
        refreshInterval: 30000, // 30 seconds
        exportInProgress: false,
        dashboardStats: {
          totalStudents: 0,
          activeExams: 0,
          totalAlerts: 0,
          averageFocusPercentage: 0,
          alertsByLevel: {
            NORMAL: 0,
            DISTRACTED: 0,
            WARNING: 0,
            CRITICAL_ALERT: 0,
          },
        },

        // Actions
        loadStudentSessions: async () => {
          set({ isLoading: true, error: null });
          
          try {
            const response = await apiClient.getStudentSessions();
            
            if (response.success && response.data) {
              set({
                studentSessions: response.data,
                isLoading: false,
                lastRefresh: Date.now(),
                error: null,
              });
              
              get().getDashboardStats();
              
              // Setup real-time updates if enabled
              if (get().realTimeUpdates) {
                get().enableRealTimeUpdates();
              }
            } else {
              throw new Error('Failed to load student sessions');
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load sessions';
            set({
              isLoading: false,
              error: errorMessage,
            });
            throw error;
          }
        },

        refreshStudentData: async () => {
          try {
            const response = await apiClient.getStudentSessions();
            
            if (response.success && response.data) {
              set({
                studentSessions: response.data,
                lastRefresh: Date.now(),
                error: null,
              });
              
              get().getDashboardStats();
            }
          } catch (error) {
            console.error('Failed to refresh student data:', error);
            set({ error: 'Failed to refresh data' });
          }
        },

        selectStudent: (sessionId: string) => {
          set(state => ({
            selectedStudents: new Set([...state.selectedStudents, sessionId]),
          }));
        },

        selectMultipleStudents: (sessionIds: string[]) => {
          set(state => ({
            selectedStudents: new Set([...state.selectedStudents, ...sessionIds]),
          }));
        },

        deselectStudent: (sessionId: string) => {
          set(state => {
            const newSelection = new Set(state.selectedStudents);
            newSelection.delete(sessionId);
            return { selectedStudents: newSelection };
          });
        },

        clearSelection: () => {
          set({ selectedStudents: new Set() });
        },

        focusOnStudent: (sessionId: string) => {
          set({ focusedStudent: sessionId });
        },

        unfocusStudent: () => {
          set({ focusedStudent: null });
        },

        setViewMode: (mode: ViewMode) => {
          set({ viewMode: mode });
        },

        setSorting: (field: SortField, order?: SortOrder) => {
          const currentState = get();
          const newOrder = order || (currentState.sortField === field && currentState.sortOrder === 'asc' ? 'desc' : 'asc');
          
          set({
            sortField: field,
            sortOrder: newOrder,
          });
        },

        setFilters: (filters: Partial<FilterOptions>) => {
          set(state => ({
            filters: { ...state.filters, ...filters },
          }));
        },

        resetFilters: () => {
          set({
            filters: {
              alertLevel: 'all',
              examStatus: 'all',
              focusThreshold: 0,
              searchTerm: '',
            },
          });
        },

        enableRealTimeUpdates: () => {
          // Instructor dashboard uses polling instead of WebSocket
          // Start periodic refresh to get latest student data
          const { refreshInterval } = get();
          if (refreshIntervalId) clearInterval(refreshIntervalId);
          
          refreshIntervalId = setInterval(() => {
            get().refreshStudentData();
          }, refreshInterval);
          
          set({ realTimeUpdates: true });
        },

        disableRealTimeUpdates: () => {
          if (refreshIntervalId) {
            clearInterval(refreshIntervalId);
            refreshIntervalId = null;
          }
          
          set({ realTimeUpdates: false });
        },

        setRefreshInterval: (interval: number) => {
          set({ refreshInterval: interval });
          
          // Restart with new interval if real-time updates are enabled
          if (get().realTimeUpdates) {
            get().disableRealTimeUpdates();
            get().enableRealTimeUpdates();
          }
        },

        exportSelectedSessions: async () => {
          const { selectedStudents } = get();
          if (selectedStudents.size === 0) return;
          
          set({ exportInProgress: true, error: null });
          
          try {
            const exportPromises = Array.from(selectedStudents).map(sessionId =>
              apiClient.exportSessionData(sessionId)
            );
            
            await Promise.all(exportPromises);
            
            set({ exportInProgress: false });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Export failed';
            set({
              exportInProgress: false,
              error: errorMessage,
            });
            throw error;
          }
        },

        exportAllSessions: async () => {
          const { studentSessions } = get();
          
          set({ exportInProgress: true, error: null });
          
          try {
            const exportPromises = studentSessions.map(session =>
              apiClient.exportSessionData(session.sessionId)
            );
            
            await Promise.all(exportPromises);
            
            set({ exportInProgress: false });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Export failed';
            set({
              exportInProgress: false,
              error: errorMessage,
            });
            throw error;
          }
        },

        cleanupInactiveSessions: async () => {
          set({ isLoading: true, error: null });
          
          try {
            await apiClient.cleanupSessions();
            await get().loadStudentSessions();
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Cleanup failed';
            set({
              isLoading: false,
              error: errorMessage,
            });
            throw error;
          }
        },

        resetSession: async (sessionId: string) => {
          try {
            await apiClient.resetSession(sessionId);
            await get().refreshStudentData();
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Reset failed';
            set({ error: errorMessage });
            throw error;
          }
        },

        setError: (error: string | null) => {
          set({ error });
        },

        updateStudentData: (sessionId: string, data: Partial<StudentSessionInfo>) => {
          set(state => ({
            studentSessions: state.studentSessions.map(session =>
              session.sessionId === sessionId
                ? { ...session, ...data, lastActivity: new Date().toISOString() }
                : session
            ),
          }));
          
          get().getDashboardStats();
        },

        addMonitoringUpdate: (sessionId: string, monitoring: MonitoringResult) => {
          set(state => ({
            studentSessions: state.studentSessions.map(session =>
              session.sessionId === sessionId
                ? {
                    ...session,
                    monitoring: [...(session.monitoring || []), monitoring],
                    alertCount: monitoring.alertLevel !== 'NORMAL' ? (session.alertCount || 0) + 1 : (session.alertCount || 0),
                    focusPercentage: monitoring.focusPercentage || session.focusPercentage || 100,
                    lastActivity: new Date().toISOString(),
                  }
                : session
            ),
          }));
          
          get().getDashboardStats();
        },

        getFilteredStudents: () => {
          const { studentSessions, filters, sortField, sortOrder } = get();
          
          let filtered = studentSessions.filter(student => {
            // Search term filter
            if (filters.searchTerm) {
              const searchLower = filters.searchTerm.toLowerCase();
              if (!student.studentName.toLowerCase().includes(searchLower) &&
                  !student.sessionId.toLowerCase().includes(searchLower)) {
                return false;
              }
            }
            
            // Alert level filter
            if (filters.alertLevel !== 'all') {
              const monitoring = student.monitoring || [];
              const latestMonitoring = monitoring.length > 0 ? monitoring[monitoring.length - 1] : null;
              if (!latestMonitoring || latestMonitoring.alertLevel !== filters.alertLevel) {
                return false;
              }
            }
            
            // Exam status filter
            if (filters.examStatus !== 'all' && student.examStatus !== filters.examStatus) {
              return false;
            }
            
            // Focus threshold filter
            if (student.focusPercentage < filters.focusThreshold) {
              return false;
            }
            
            return true;
          });
          
          // Sort filtered results
          filtered.sort((a, b) => {
            let aValue: any = a[sortField];
            let bValue: any = b[sortField];
            
            // Handle special sorting cases
            if (sortField === 'lastActivity') {
              aValue = new Date(aValue).getTime();
              bValue = new Date(bValue).getTime();
            }
            
            if (typeof aValue === 'string') {
              aValue = aValue.toLowerCase();
              bValue = bValue.toLowerCase();
            }
            
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
          });
          
          return filtered;
        },

        getDashboardStats: () => {
          const { studentSessions } = get();
          
          const stats = {
            totalStudents: studentSessions.length,
            activeExams: studentSessions.filter(s => s.examStatus === 'in_progress').length,
            totalAlerts: studentSessions.reduce((sum, s) => sum + s.alertCount, 0),
            averageFocusPercentage: Math.round(
              studentSessions.reduce((sum, s) => sum + s.focusPercentage, 0) / studentSessions.length || 0
            ),
            alertsByLevel: {
              NORMAL: 0,
              DISTRACTED: 0,
              WARNING: 0,
              CRITICAL_ALERT: 0,
            },
          };
          
          // Calculate alerts by level from recent monitoring data
          studentSessions.forEach(student => {
            // Safely access monitoring array with fallback
            const monitoring = student.monitoring || [];
            const latestMonitoring = monitoring.length > 0 ? monitoring[monitoring.length - 1] : null;
            if (latestMonitoring && latestMonitoring.alertLevel) {
              stats.alertsByLevel[latestMonitoring.alertLevel]++;
            }
          });
          
          set({ dashboardStats: stats });
        },
      }),
      {
        name: 'quizsecure-instructor',
        partialize: (state) => ({
          viewMode: state.viewMode,
          sortField: state.sortField,
          sortOrder: state.sortOrder,
          filters: state.filters,
          realTimeUpdates: state.realTimeUpdates,
          refreshInterval: state.refreshInterval,
        }),
      }
    ),
    { name: 'InstructorStore' }
  )
);

// Cleanup function for component unmounting
export const cleanupInstructorStore = () => {
  const { disableRealTimeUpdates } = useInstructorStore.getState();
  disableRealTimeUpdates();
};

// Utility functions for instructor dashboard
export const getStudentStatusColor = (student: StudentSessionInfo): string => {
  if (student.examStatus === 'completed') return 'text-state-nominal bg-state-nominal-soft';
  if (student.alertCount > 5) return 'text-state-critical bg-state-critical-soft';
  if (student.focusPercentage < 50) return 'text-state-warning bg-state-warning-soft';
  return 'text-content-secondary bg-surface-nested';
};

export const getStudentRiskLevel = (student: StudentSessionInfo): 'low' | 'medium' | 'high' => {
  const monitoring = student.monitoring || [];
  const alertRatio = (student.alertCount || 0) / Math.max(1, monitoring.length);
  
  if (alertRatio > 0.3 || (student.focusPercentage || 100) < 40) return 'high';
  if (alertRatio > 0.15 || (student.focusPercentage || 100) < 70) return 'medium';
  return 'low';
};

export const formatLastActivity = (lastActivity: string): string => {
  const now = Date.now();
  const activity = new Date(lastActivity).getTime();
  const diff = now - activity;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
};